import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import { FileText, Mail, Clock, Plus, Search } from 'lucide-react';
import React from 'react';

import { useStore } from 'hooks/useStore';
import { OOCSubmission, OOCTabType, OOCDateFilter, OOC_STATUS_OPTIONS } from '../oocUpdated.type';
import { isCurrentUser } from 'utils/userUtils';
import {
  useOOCDashboardLogic,
  getStatusBadgeClass,
  getStatusLabel,
  CONTRACT_TYPE_OPTIONS,
  DATE_DROPDOWN_OPTIONS,
  SummaryCardConfig,
} from './oocDashboard.logic';
import styles from './oocDashboard.module.scss';

const ICON_MAP: Record<SummaryCardConfig['iconType'], React.ReactNode> = {
  fileText: <FileText size={20} />,
  mail: <Mail size={20} />,
  clock: <Clock size={20} />,
};

const ActionCell = observer(({ row, loggedInUserName }: { row: OOCSubmission; loggedInUserName: string }) => {
  const { OOCStoreUpdated: OOCStore } = useStore();
  if (OOCStore.savingSubmissionId === row.id) {
    return <Button label="Saving..." size="small" className={`${styles.actionBtn} ${styles.editBtn}`} disabled />;
  }
  if (row.status === 'draft' && isCurrentUser(loggedInUserName, row.submittedBy)) {
    return <Button label="Edit" size="small" className={`${styles.actionBtn} ${styles.editBtn}`} onClick={() => OOCStore.openSubmissionForEdit(row.id)} />;
  }
  return <Button label="View" size="small" className={`${styles.actionBtn} ${styles.viewBtn}`} onClick={() => OOCStore.openSubmissionForEdit(row.id)} />;
});

const OOCDashboard = observer(() => {
  const { OOCStore, displayedSubmissions, loggedInUserName, handleCardClick, handleCreateNew, summaryCardConfigs } = useOOCDashboardLogic();

  const oocIdTemplate = (row: OOCSubmission) => (
    <span className={styles.oocIdCell} onClick={() => OOCStore.openSubmissionForEdit(row.id)}>
      {row.id}
    </span>
  );

  const contractTypesTemplate = (row: OOCSubmission) => (
    <div className={styles.contractChips}>
      {row.contractTypes.map((ct) => (
        <span key={ct} className={styles.contractChip}>
          {ct}
        </span>
      ))}
    </div>
  );

  const statusTemplate = (row: OOCSubmission) => (
    <span className={`${styles.statusBadge} ${getStatusBadgeClass(row.status, styles)}`}>
      {getStatusLabel(row.status)}
    </span>
  );

  const actionTemplate = (row: OOCSubmission) => <ActionCell row={row} loggedInUserName={loggedInUserName} />;

  if (OOCStore.isLoading && OOCStore.submissions.length === 0) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.skeletonCards}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className={styles.skeletonCard} />
          ))}
        </div>
        <Skeleton height="3rem" />
        <Skeleton height="20rem" />
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.summaryCards}>
        {summaryCardConfigs.map((card) => (
          <div
            key={card.key}
            className={`${styles.summaryCard} ${
              card.key !== 'history' && OOCStore.activeCardFilter === card.key ? styles.active : ''
            }`}
            onClick={() => handleCardClick(card.key)}
          >
            <div className={`${styles.cardIcon} ${styles[card.colorClass]}`}>
              {ICON_MAP[card.iconType]}
            </div>
            <div className={styles.cardInfo}>
              <span className={styles.cardCount}>{card.count}</span>
              <span className={styles.cardLabel}>{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={16} className={styles.searchIcon} />
          <InputText
            value={OOCStore.searchQuery}
            onChange={(e) => OOCStore.setSearchQuery(e.target.value)}
            placeholder="Search by NDC, Concept, or Molecule..."
            style={{ paddingLeft: '2.5rem', width: '100%' }}
          />
        </div>

        <Dropdown
          value={OOCStore.statusFilter}
          options={OOC_STATUS_OPTIONS}
          onChange={(e) => OOCStore.setStatusFilter(e.value)}
          className={styles.filterDropdown}
          placeholder="All Statuses"
        />

        <Dropdown
          value={OOCStore.contractTypeFilter}
          options={CONTRACT_TYPE_OPTIONS}
          onChange={(e) => OOCStore.setContractTypeFilter(e.value as OOCTabType | 'all')}
          className={styles.filterDropdown}
          placeholder="All Contract Types"
        />

        <Dropdown
          value={OOCStore.dateFilter}
          options={DATE_DROPDOWN_OPTIONS}
          onChange={(e) => OOCStore.setDateFilter(e.value as OOCDateFilter)}
          className={styles.filterDropdown}
          placeholder="This Month"
        />

        <Button
          label="New OOC"
          icon={<Plus size={16} style={{ marginRight: 6 }} />}
          className={styles.newOocBtn}
          onClick={handleCreateNew}
        />
      </div>

      <div className={styles.tableContainer}>
        <DataTable
          value={displayedSubmissions}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          emptyMessage="No OOC submissions found."
          loading={OOCStore.isLoading}
          stripedRows
          size="small"
        >
          <Column header="OOC ID" body={oocIdTemplate} sortable field="id" style={{ minWidth: '120px' }} />
          <Column header="Date" field="submissionDate" sortable style={{ minWidth: '110px' }} />
          <Column header="Submitted By" field="submittedBy" sortable style={{ minWidth: '140px' }} />
          <Column header="Contract Types" body={contractTypesTemplate} style={{ minWidth: '140px' }} />
          <Column header="# NDCs" field="ndcCount" sortable style={{ minWidth: '80px', textAlign: 'center' }} />
          <Column header="Status" body={statusTemplate} sortable field="status" style={{ minWidth: '130px' }} />
          <Column header="Action" body={actionTemplate} style={{ minWidth: '90px' }} />
        </DataTable>

        <div className={styles.tableFooter}>
          Showing {displayedSubmissions.length} of {OOCStore.submissions.length} submissions
        </div>
      </div>
    </div>
  );
});

export default OOCDashboard;
